/**
 * 普通滚动列表组件
 */
import classnames from 'classnames';
import React, { HTMLAttributes, ReactNode, useEffect, useState } from 'react';
import IconFont from '@meitu/xdesign-icon-font';
import { useLocaleStore } from '../i18n';
import Scrollbars from '../scrollbars';
import './index.less';

interface IProps<T> extends HTMLAttributes<HTMLDivElement> {
    /**
     * 加载中
     */
    loading: boolean;
    /**
     * 加载中文案
     */
    loadingText?: string;
    /**
     * 是否还有更多
     */
    hasMore: boolean;
    /**
     * 没有更多数据文案
     */
    noMoreText?: string;
    /**
     * 自定义没有更多数据的节点渲染
     */
    customNoMoreNode?: () => ReactNode;
    /**
     * 空态文案
     */
    emptyText?: string;
    /**
     * 渲染节点
     */
    beforeEmpty?: () => ReactNode;
    /**
     * 空态节点
     */
    emptyNode?: () => ReactNode;
    /**
     * 距离容器底部多少px时触发加载更多，默认100px
     */
    reachBottomDis?: number;
    /**
     * 列表请求接口
     */
    nextPageRequest: () => void;
    /**
     * 列表数据源
     */
    dataSource: T[];
    renderItem: (data: T, index: number) => ReactNode;
    /**
     * 是否瀑布流
     */
    isWaterfall?: boolean;
    /**
     * 瀑布流列数
     */
    columns?: number;
    getItemHeight?: (data: T) => number;

    id?: string;
    bodyStyle?: React.CSSProperties;
    bodyClassName?: string;

    /**
     * 懒加载 多少数据 可参考https://www.npmjs.com/package/react-lazyload offset
     */
    lazyLoadOffSet?: number;

    // 顶部预置内容
    preContent?: JSX.Element;

    // 是否展示"加载更多"文案
    showLoadMore?: boolean;
}

const List = <T,>(props: IProps<T>) => {
    const { $t } = useLocaleStore();

    const {
        style,
        bodyStyle,
        bodyClassName,
        className,
        loading = true,
        loadingText = $t((v) => v.list.loadingText),
        hasMore = true,
        noMoreText = $t((v) => v.list.noMoreText),
        emptyText = $t((v) => v.list.emptyText),
        nextPageRequest,
        reachBottomDis = 100,
        // lazyLoadOffSet = 1000,
        dataSource = [],
        renderItem = () => {},
        isWaterfall = false,
        columns = 2,
        getItemHeight = () => {},
        id,
        emptyNode,
        beforeEmpty = () => {},
        customNoMoreNode,
        preContent = null,
        showLoadMore = false,
    } = props;
    // 保存瀑布流分组数据
    const [list, setList] = useState<T[][]>([]);
    const [isNextRequest, setIsNextRequest] = useState<boolean>(false);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleScroll = (e: any) => {
        // 容器高度
        const clientHeight = e.target.clientHeight;
        // 滚动距离
        const scrollTop = e.target.scrollTop;
        // 内容高度
        const scrollHeight = e.target.scrollHeight;

        if (loading) return;

        // 触底加载更多
        if (hasMore && clientHeight + scrollTop >= scrollHeight - reachBottomDis) {
            nextPageRequest();
            setIsNextRequest(true);
        }
    };

    useEffect(() => {
        if (hasMore) return;
        setIsNextRequest(true);
    }, [hasMore]);

    useEffect(() => {
        if (!isWaterfall) return;

        const temp: T[][] = Array.from({ length: columns }, () => []);
        const heightList = Array.from({ length: columns }, () => 0);
        // 分组
        dataSource.forEach((item) => {
            // 找出最小高度值
            const min = Math.min(...heightList);
            // 找出最小高度值对应的索引
            const index = heightList.indexOf(min);
            // 将数据堆放在该索引对应的列数组中
            temp[index].push(item);
            const curHeight = getItemHeight(item) || 0;
            // 计算存放数据后的列高度
            heightList[index] = heightList[index] + curHeight;
        });

        setList(temp);
    }, [dataSource, columns]);

    return (
        <Scrollbars
            className={classnames('v-list-component', className)}
            style={{ ...style }}
            onScroll={handleScroll}
            id={id}
        >
            {beforeEmpty && beforeEmpty()}
            {!isWaterfall && (
                <div className={bodyClassName} style={{ ...bodyStyle }}>
                    {preContent}
                    {dataSource.map((item, index) => {
                        return (
                            <div key={index} onContextMenu={(e) => e.preventDefault()}>
                                {renderItem(item, index)}
                            </div>
                        );
                    })}
                </div>
            )}
            {isWaterfall ? (
                <>
                    {preContent}
                    <div className="v-list-component__waterfall">
                        {list.map((coulmn, index) => (
                            <div key={index}>
                                {coulmn.map((item, idx) => {
                                    return (
                                        <div key={idx} onContextMenu={(e) => e.preventDefault()}>
                                            {renderItem(item, idx)}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </>
            ) : null}

            {showLoadMore && hasMore && !loading && dataSource.length ? (
                <div className="v-list-component__loadMore" onClick={() => nextPageRequest()}>
                    {$t((v) => v.list.loadMoreText)}
                </div>
            ) : null}
            {loading ? <div className="v-list-component__loading">{loadingText}</div> : null}
            {!loading && dataSource.length && !hasMore && isNextRequest ? (
                <div className="v-list-component__noMore">{customNoMoreNode ? customNoMoreNode() : noMoreText}</div>
            ) : null}
            {!hasMore && !dataSource.length && !loading ? (
                <div className="v-list-component__empty">
                    <div className="v-list-component__empty__content">
                        {emptyNode ? (
                            emptyNode()
                        ) : (
                            <>
                                <IconFont name="EmptyDrawer" fontSize={45} />
                                {emptyText}
                            </>
                        )}
                    </div>
                </div>
            ) : null}
        </Scrollbars>
    );
};

export default List;
